import PropTypes from 'prop-types';
import { useIsMobile } from '@bwoty-web/ui-kit/hooks';
import { UI_CHECKMARK } from '@bwoty-web/ui-kit/icons';
import { ArrowLink, BaseCard, Button, Icon, Rating, TripadvisorRating, Typography } from '@bwoty-web/ui-kit';

import { standaloneUrlHandler } from '../../../utils/url/url';
import { getGeographicalString } from '../../../utils/mappers/geographical';

import './compactHotelItem.scss';

const CompactHotelItem = ({
  hotel: {
    boardCode,
    boardCodeDescription,
    checkIn,
    checkOut,
    geographical,
    hasAlternatives,
    hotelImage,
    hotelRating,
    introText,
    links,
    name,
    quickFacts,
    roomDescription,
    tripAdvisor,
    url,
    usps,
  },
  isSummary = false,
  labels,
}) => {
  const isMobile = useIsMobile();

  const period = `${checkIn} - ${checkOut}`;
  const boardDescription = boardCode === '0' ? labels?.noMealsIncluded : boardCodeDescription;
  const hotelPopUpUrl = url ? `${url}?pageTemplate=popUpPage&qf=popUpPage` : '';

  return (
    <BaseCard
      image={hotelImage}
      imagePosition={isMobile ? 'top' : 'left'}
      imageAspectRatio={isMobile ? 16 / 9 : 4 / 3}
    >
      <section className="iw-compact-hotel-item__section-container">
        <div className="iw-compact-hotel-item__top-section">
          <div className="iw-compact-hotel-item__content-container">
            <Typography
              as="p"
              variant="mini"
              className="iw-compact-hotel-item__period"
            >
              {period}
            </Typography>
            {url ? (
              <a
                className="iw-compact-hotel-item__header-link"
                href={hotelPopUpUrl}
              >
                <h3 className="iw-compact-hotel-item__header">{name}</h3>
              </a>
            ) : (
              <h3 className="iw-compact-hotel-item__header">{name}</h3>
            )}
            <p className="iw-compact-hotel-item__subheader">{getGeographicalString(geographical)}</p>
            <div className="iw-compact-hotel-item__rating-container">
              {!!hotelRating && (
                <Rating
                  rating={hotelRating}
                  size="s"
                />
              )}
              {tripAdvisor?.averageRating && <TripadvisorRating rating={tripAdvisor?.averageRating} />}
            </div>
            {!isSummary && introText && <p className="iw-compact-hotel-item__description">{introText}</p>}
            {!isSummary && quickFacts?.length > 0 && (
              <ul className="iw-compact-hotel-item__bullets">
                {quickFacts.map(({ id, name: factName, value }) => (
                  <li
                    key={id}
                    className="iw-compact-hotel-item__bullet"
                  >
                    {`${factName}: ${value}`}
                  </li>
                ))}
              </ul>
            )}
            {!isSummary && usps?.length > 0 && (
              <ul className="iw-compact-hotel-item__ksps">
                {usps.map((usp) => (
                  <li
                    key={usp}
                    className="iw-compact-hotel-item__ksp"
                  >
                    <Icon path={UI_CHECKMARK} />
                    {usp}
                  </li>
                ))}
              </ul>
            )}
            {url && labels?.readMoreHotel && (
              <ArrowLink
                className="iw-compact-hotel-item__read-more-link"
                href={hotelPopUpUrl}
              >
                {labels?.readMoreHotel}
              </ArrowLink>
            )}
          </div>
          {hasAlternatives && isSummary && (
            <Button
              className="iw-compact-hotel-item__change-hotel-button"
              href={standaloneUrlHandler(links?.combinationChangeHotel)}
              name="change-first-combo-hotel"
              size="s"
            >
              {labels?.changeHotel}
            </Button>
          )}
        </div>
        {isSummary && (
          <div className="iw-compact-hotel-item__bottom-section">
            <div className="iw-compact-hotel-item__room-container">
              <p className="iw-compact-hotel-item__label">{labels?.roomGroupTitle}</p>
              <p className="iw-compact-hotel-item__room-meal">
                {roomDescription?.map((room, i) => {
                  return (
                    <span key={room?.description}>{`${i > 0 ? ', ' : ''}${
                      room?.description
                    } (${room?.count} ${labels?.numberOfSuffix})`}</span>
                  );
                })}
              </p>
            </div>
            <div className="iw-compact-hotel-item__meal-container">
              <p className="iw-compact-hotel-item__label">{labels?.boardDescriptionTitle}</p>
              <p className="iw-compact-hotel-item__room-meal">{boardDescription}</p>
            </div>
            <ArrowLink
              className="iw-compact-hotel-item__change-room-meal-link"
              href={standaloneUrlHandler(links?.combinationRoomSelect)}
            >
              {labels?.changeRoomAndFood}
            </ArrowLink>
          </div>
        )}
      </section>
    </BaseCard>
  );
};

CompactHotelItem.propTypes = {
  hotel: PropTypes.shape({
    boardCode: PropTypes.string,
    boardCodeDescription: PropTypes.string,
    boardDescription: PropTypes.string,
    checkIn: PropTypes.string,
    checkOut: PropTypes.string,
    geographical: PropTypes.shape({
      resort: PropTypes.shape({ name: PropTypes.string }),
      area: PropTypes.shape({ name: PropTypes.string }),
    }),
    hasAlternatives: PropTypes.bool,
    hotelImage: PropTypes.shape({ path: PropTypes.string }),
    hotelRating: PropTypes.number,
    introText: PropTypes.string,
    links: PropTypes.shape({
      combinationChangeHotel: PropTypes.string,
      combinationRoomSelect: PropTypes.string,
    }),
    name: PropTypes.string,
    quickFacts: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        value: PropTypes.string,
      }),
    ),
    roomDescription: PropTypes.arrayOf(PropTypes.shape({ description: PropTypes.string })),
    selectedRoomGroup: PropTypes.shape({
      changeRoomGroupLink: PropTypes.string,
      rooms: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    tripAdvisor: PropTypes.shape({ averageRating: PropTypes.number }),
    url: PropTypes.string,
    usps: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  isSummary: PropTypes.bool,
  labels: PropTypes.shape({
    boardDescriptionTitle: PropTypes.string,
    changeHotel: PropTypes.string,
    changeRoomAndFood: PropTypes.string,
    noMealsIncluded: PropTypes.string,
    numberOfSuffix: PropTypes.string,
    readMoreHotel: PropTypes.string,
    roomGroupTitle: PropTypes.string,
  }).isRequired,
};

export default CompactHotelItem;
